/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { compose } from 'recompose';

import Retracked from 'js/app/retracked';
import redirect from 'js/lib/coursera.redirect';
import { useParams } from 'js/lib/useRouter';
import user from 'js/lib/user';

import CTABanner from 'bundles/browse/components/CTABanner';
import { style as CollectionStyle } from 'bundles/browse/components/Collection';
import OfferExplanationTableStyle from 'bundles/browse/components/OfferExplanationTableStyle';
import { style as BrowseDegreeCardStyle } from 'bundles/browse/components/ProductCard/BrowseDegreeCard';
import { legacyStyle as BrowseProductCardStyle } from 'bundles/browse/components/ProductCard/BrowseProductCard';
import { style as CardSectionStyle } from 'bundles/browse/components/ProductCard/CardSection';
import { style as PartnerLogoStyle } from 'bundles/browse/components/ProductCard/PartnerLogo';
import { style as TagBadgeStyle } from 'bundles/browse/components/ProductCard/TagBadge';
import { style as TopicAndSkillsStyle } from 'bundles/browse/components/TopicAndSkills';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import LegacyEnterpriseReturnToProgramBanner from 'bundles/composable-page-header-consumer/banners/LegacyEnterpriseReturnToProgramBanner';
import DefaultHeader from 'bundles/composable-page-header-consumer/header-renderers/DefaultHeader';
import UniversalPageFeatures from 'bundles/composable-page-header/components/UniversalPageFeatures';
import PageFooter from 'bundles/page-footer/components/PageFooter';
import { style as MobileFeaturedTopicsStyle } from 'bundles/page-header/components/mobile/MobileFeaturedTopics';
import { RedirectError } from 'bundles/ssr/lib/errors/directives';
import inServerContext from 'bundles/ssr/util/inServerContext';
import InteractionGuard from 'bundles/unified-common/components/InteractionGuard';
import {
  GlobalCoachChatStateProvider,
  useGlobalCoachChatState,
} from 'bundles/unified-common/providers/GlobalCoachChatStateProvider';
import NavAiSparkleProvider, { useNavAiSparkle } from 'bundles/unified-common/providers/NavAiSparkleProvider';
import withMarketingConsent from 'bundles/user-consent/components/withMarketingConsent';

import 'css!bundles/browse/styl/index';

const GlobalCoachChat = React.lazy(
  () => import('bundles/unified-common/components/coach-on-discovery/GlobalCoachChat')
);

const PAGE_HEADER_HEIGHT = 60;

const styles = {
  browseApp: css`
    .c-phoenix-template-header,
    .c-phoenix-template-footer,
    .c-error-body,
    .c-cs-layout {
      max-width: 960px;
      margin: 0 auto;
    }

    ${MobileFeaturedTopicsStyle}
    ${TopicAndSkillsStyle}
  ${CollectionStyle}
  ${BrowseDegreeCardStyle}
  ${BrowseProductCardStyle}
  ${CardSectionStyle}
  ${PartnerLogoStyle}
  ${TagBadgeStyle}
  ${OfferExplanationTableStyle}
  `,
  browseWrapperApp: css`
    min-height: calc(100vh - ${PAGE_HEADER_HEIGHT}px) !important;
    background-color: var(--cds-color-grey-25) !important;
  `,
  browseWrapper2App: css`
    min-height: calc(100vh - ${PAGE_HEADER_HEIGHT}px) !important;
    background-color: var(--cds-color-neutral-background-primary) !important;
  `,
};

export const BrowseApp = ({ children }: React.PropsWithChildren) => {
  const { domainId } = useParams();
  const { showNavAiSparkle } = useNavAiSparkle();
  const { coachOpen, pageChatType } = useGlobalCoachChatState();
  const isFullscreenCoachChatOpen = coachOpen && pageChatType === null;

  if (domainId === 'degrees-and-professional-certificates') {
    const redirectUrl = '/browse';
    if (inServerContext) {
      throw new RedirectError(302, redirectUrl);
    } else {
      redirect.setLocation(redirectUrl);
    }
  }

  return (
    <div className="rc-BrowseApp">
      <DefaultHeader
        showMetaNav
        banners={[
          {
            name: 'LegacyEnterpriseReturnToProgramBanner',
            component: <LegacyEnterpriseReturnToProgramBanner />,
          },
        ]}
      />
      <UniversalPageFeatures />
      {showNavAiSparkle && (
        <ErrorBoundaryWithTagging componentName="GlobalCoachChat">
          <React.Suspense fallback={null}>
            <GlobalCoachChat />
          </React.Suspense>
        </ErrorBoundaryWithTagging>
      )}
      <InteractionGuard preventInteraction={isFullscreenCoachChatOpen}>
        <main id="main" className="browse-children-wrapper">
          {children}
          {/* Added a wrapper to the CTA so we can track the element visibility on the page in SSR and CSR */}
          {!user.isAuthenticatedUser() && (
            <div className="browse-cta-wrapper">
              <CTABanner />
            </div>
          )}
        </main>
        <PageFooter />
      </InteractionGuard>
    </div>
  );
};

type BrowseThemeProviderProps = {
  isBrowseV2: boolean;
  children: JSX.Element;
};

export const BrowseThemeProvider = (props: BrowseThemeProviderProps) => {
  const { isBrowseV2, children } = props;
  return <div css={isBrowseV2 ? styles.browseWrapper2App : styles.browseWrapperApp}>{children}</div>;
};

export const browseThemeProvider =
  <T extends {}>(Component: React.ComponentType<T & any>) =>
  (props: T) => (
    <div className="rc-BrowseThemeProvider" css={styles.browseApp}>
      <Component {...props} />
    </div>
  );

export const navAiSparkleProvider =
  <T extends {}>(Component: React.ComponentType<T & any>) =>
  (props: T) => (
    <GlobalCoachChatStateProvider>
      <NavAiSparkleProvider>
        <Component {...props} />
      </NavAiSparkleProvider>
    </GlobalCoachChatStateProvider>
  );

export default compose<{}, React.PropsWithChildren>(
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'browse',
      page: 'browse',
    },
  })),
  browseThemeProvider,
  withMarketingConsent,
  navAiSparkleProvider
)(BrowseApp);
